import { useEffect, useState, useRef } from 'react';
import { T, useTheme, Ico } from '../../../design';
import { validateImageFile, MAX_IMAGES_PER_ITEM } from '../imageValidation';

export interface ImagePickerProps {
  files: File[];
  onChange: (files: File[]) => void;
  /** Kaldes med en besked når en valgt fil afvises (vises som toast af forælderen). */
  onReject: (message: string) => void;
  max?: number;
}

export function ImagePicker({ files, onChange, onReject, max = MAX_IMAGES_PER_ITEM }: ImagePickerProps) {
  const t = useTheme();
  const inputRef = useRef<HTMLInputElement>(null);

  const [previews, setPreviews] = useState<string[]>([]);
  // Object-URL previews SKAL skabes som en side-effekt (det er ekstern ressource-
  // håndtering, ikke afledt render-state). Effekt-mønstret er StrictMode-sikkert:
  // skab + revoke symmetrisk. En useMemo-variant ville dobbelt-skabe og lække én
  // URL-sæt pr. ændring i dev under StrictMode — derfor er set-state-in-effect-
  // reglen en falsk-positiv her, og bevidst slået fra på linjen nedenfor.
  useEffect(() => {
    const urls = files.map((f) => URL.createObjectURL(f));
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPreviews(urls);
    return () => urls.forEach((url) => URL.revokeObjectURL(url));
  }, [files]);

  function handleFiles(list: FileList | null) {
    if (!list) return;
    const incoming = Array.from(list);
    const room = max - files.length;
    const accepted: File[] = [];
    for (const f of incoming) {
      if (accepted.length >= room) { onReject(`Maks. ${max} billeder pr. vare.`); break; }
      const err = validateImageFile(f);
      if (err) { onReject(err); continue; }
      accepted.push(f);
    }
    if (accepted.length) onChange([...files, ...accepted]);
    if (inputRef.current) inputRef.current.value = '';
  }

  return (
    <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap' }}>
      {files.map((f, i) => (
        <div key={`${f.name}-${i}`} style={{ position: 'relative', width: 72, height: 90, borderRadius: t.radius, overflow: 'hidden', border: `1px solid ${T.border}` }}>
          {previews[i]
            ? <img src={previews[i]} alt={`Billede ${i + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            : <div style={{ width: '100%', height: '100%', background: T.tertiary }} />}
          <button
            type="button"
            aria-label={`Fjern billede ${i + 1}`}
            onClick={() => onChange(files.filter((_, j) => j !== i))}
            style={{ position: 'absolute', top: 4, right: 4, width: 22, height: 22, borderRadius: 999, border: 0, background: T.dark, color: T.onAccent, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
          >
            <Ico.x size={12} />
          </button>
        </div>
      ))}
      {files.length < max && (
        <button
          type="button"
          aria-label="Tilføj billede"
          onClick={() => inputRef.current?.click()}
          style={{ width: 72, height: 90, borderRadius: t.radius, border: `1.5px dashed ${T.border}`, background: T.surface, color: T.fg3, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 5, cursor: 'pointer', fontFamily: T.font }}
        >
          <Ico.camera size={20} />
          <span style={{ fontSize: 10.5, fontWeight: 700 }}>Tilføj</span>
        </button>
      )}
      <input
        ref={inputRef}
        data-testid="image-input"
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        multiple
        onChange={(e) => handleFiles(e.target.files)}
        style={{ display: 'none' }}
      />
    </div>
  );
}
